import { Link, useNavigate } from "react-router-dom";
import { productUrl } from "../../utils/productUrl";
import { FiX, FiShoppingBag, FiTrash2, FiArrowLeft } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { imageUrl } from "../../utils/imageUrl";
import { toman, toPersianNumber } from "../../utils/persian";
import { incQuantity, decQuantity, removeItem, syncCart } from "../../store/reducers/cartReducer";
import { useValidateCartMutation } from "../../store/services/checkoutService";
import { useFeedback } from "../../context/AppFeedbackContext";
import Quantity from "./Quantity";

const getCartItemImage = (item) =>
  item?.image1 || item?.main_image || item?.image || item?.thumbnail || item?.gallery?.[0] || "";

const CartDrawer = ({ open, onClose, compact = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const { cart, total, items } = useSelector((state) => state.cartReducer);
  const { userToken } = useSelector((state) => state.authReducer);
  const [validateCart, validateCartResponse] = useValidateCartMutation();

  const inc = (item) => {
    dispatch(
      incQuantity({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );
  };

  const dec = (item) => {
    dispatch(
      decQuantity({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );
  };

  const remove = (item) => {
    dispatch(
      removeItem({
        _id: item._id,
        variant_id: item.variant_id || item.product_variant_id || null,
        product_variant_id: item.product_variant_id || item.variant_id || null,
        color: item.color,
        size: item.size
      })
    );
  };

  const notifyCartSync = (res) => {
    const summary = res?.summary || {};
    const removedCount = Number(summary.removed_count || 0);
    const quantityChangedCount = Number(summary.quantity_changed_count || 0);

    if (removedCount > 0) {
      notify("کالای ناموجود حذف شد.", "warning", "سبد خرید");
      return;
    }

    if (quantityChangedCount > 0) {
      notify("تعداد کالاها به‌روز شد.", "warning", "سبد خرید");
    }
  };

  const goCheckout = async () => {
    if (!cart.length || validateCartResponse.isLoading) return;

    try {
      const res = await validateCart({ cart }).unwrap();

      if (Array.isArray(res.cart)) {
        dispatch(syncCart(res.cart));
      }

      notifyCartSync(res);

      if (Array.isArray(res.cart) && res.cart.length === 0) {
        return;
      }

      onClose?.();

      if (userToken) {
        navigate("/checkout");
      } else {
        sessionStorage.setItem("redirectAfterLogin", "/checkout");
        navigate("/login", { state: { from: "/checkout" } });
      }
    } catch (err) {
      notify(err?.data?.message || "سبد بررسی نشد.", "error", "سبد بررسی نشد");
    }
  };

  const shownItems = compact ? cart.slice(0, 4) : cart;
  const isSingleMiniCart = compact && shownItems.length === 1;

  if (compact) {
    return (
      <div className={`mini-cart-popover ${isSingleMiniCart ? "mini-cart-one" : ""}`}>
        <div className="mini-cart-popover-head">
          <strong>سبد خرید</strong>
          <span>{toPersianNumber(items)} کالا</span>
        </div>

        {cart.length ? (
          <>
            <div className={isSingleMiniCart ? "mini-cart-single-product" : "mini-cart-popover-list"}>
              {shownItems.map((item) => (
                <div className={isSingleMiniCart ? "mini-cart-single-item" : "mini-cart-popover-item"} key={`${item._id}-${item.color || "no-color"}-${item.size || "no-size"}-${item.variant_id || ""}`}>
                  <img src={imageUrl(getCartItemImage(item))} alt={item.title || item.name || ""} />

                  <div>
                    <strong>{item.title || item.name}</strong>
                    <span>
                      {item.color || "بدون رنگ"}
                      {item.size ? ` | سایز ${toPersianNumber(item.size)}` : ""}
                    </span>
                    <small>
                      {toPersianNumber(item.quantity)} × {toman(item.sale_price || item.price)}
                    </small>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > shownItems.length && (
              <p className="mini-cart-more">{toPersianNumber(cart.length - shownItems.length)} مورد دیگر در سبد شماست.</p>
            )}

            <div className="mini-cart-popover-total">
              <span>جمع سبد</span>
              <strong>{toman(total)}</strong>
            </div>

            <div className="mini-cart-shipping-note">
              <span>ارسال</span>
              <strong>مرحله بعد</strong>
            </div>

            <div className="mini-cart-popover-actions">
              <Link to="/cart">مشاهده سبد</Link>
              <button type="button" onClick={goCheckout} disabled={validateCartResponse.isLoading}>
                {validateCartResponse.isLoading ? "بررسی..." : "ادامه خرید"}
              </button>
            </div>
          </>
        ) : (
          <div className="mini-cart-empty">
            <FiShoppingBag size={26} />
            <p>سبد خرید شما خالی است.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`cart-drawer-backdrop ${open ? "active" : ""}`} onClick={onClose} />

      <aside className={`cart-drawer ${open ? "active" : ""}`} aria-hidden={!open}>
        <div className="cart-drawer-head">
          <div>
            <span>سبد خرید شما</span>
            <h3>{toPersianNumber(items)} کالا در سبد</h3>
          </div>

          <button type="button" onClick={onClose} aria-label="بستن سبد خرید">
            <FiX size={21} />
          </button>
        </div>

        {cart.length ? (
          <>
            <div className="cart-drawer-list">
              {cart.map((item) => (
                <article className="cart-drawer-item" key={`${item._id}-${item.color || "no-color"}-${item.size || "no-size"}-${item.variant_id || ""}`}>
                  <Link to={productUrl(item)} onClick={onClose} className="cart-drawer-image">
                    <img src={imageUrl(getCartItemImage(item))} alt={item.title || item.name || ""} />
                  </Link>

                  <div className="cart-drawer-info">
                    <Link to={productUrl(item)} onClick={onClose}>
                      {item.title || item.name}
                    </Link>

                    <p>
                      {item.color ? `رنگ: ${item.color}` : "رنگ انتخاب نشده"}
                      {item.size ? ` | سایز: ${toPersianNumber(item.size)}` : ""}
                    </p>

                    <strong>{toman(item.sale_price || item.price)}</strong>

                    <div className="cart-drawer-row">
                      <Quantity quantity={item.quantity} inc={() => inc(item)} dec={() => dec(item)} max={item.stock || item.available_stock || 0} />

                      <button type="button" onClick={() => remove(item)} aria-label="حذف از سبد">
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="cart-drawer-footer">
              <div>
                <span>جمع سبد</span>
                <strong>{toman(total)}</strong>
              </div>

              <div className="cart-drawer-shipping-note">
                <span>ارسال</span>
                <strong>مرحله بعد</strong>
              </div>

              <button
                type="button"
                onClick={goCheckout}
                disabled={validateCartResponse.isLoading}
                className="cart-drawer-checkout"
              >
                {validateCartResponse.isLoading ? "بررسی..." : "ادامه خرید"}
                <FiArrowLeft size={18} />
              </button>

              <Link to="/cart" onClick={onClose} className="cart-drawer-cart-link">
                مشاهده سبد
              </Link>
            </div>
          </>
        ) : (
          <div className="cart-drawer-empty">
            <FiShoppingBag size={34} />
            <h4>سبد خرید خالی است</h4>
            <p>محصولات مورد علاقه‌ات را انتخاب کن تا اینجا ببینی.</p>
            <Link to="/" onClick={onClose}>مشاهده محصولات</Link>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
